import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Lock, ImagePlus, Plus, Trash2, Building2, Tag, X, Edit2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { getImageUrl } from '../utils/imageUrl';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BUSINESS_CATEGORIES = [
  "Children",
  "Home & Garden",
  "Pets",
  "Professional Services",
  "Wellness & Beauty",
  "Women's Lifestyle"
];

export default function ProfilePage({ user, setUser }) {
  const [areas, setAreas] = useState([]);

  useEffect(() => {
    axios.get(`${API}/areas`).then(res => setAreas(res.data)).catch(() => {});
  }, []);
  const [formData, setFormData] = useState({
    full_name: user.full_name,
    display_name: user.display_name || '',
    display_email: user.display_email || '',
    area: user.area,
    business_name: user.business_name || '',
    business_category: user.business_category || '',
    business_description: user.business_description || '',
    phone: user.phone || '',
    website: user.website || '',
    tags: user.tags || []
  });
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [passwordDialog, setPasswordDialog] = useState(false);
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [changingPassword, setChangingPassword] = useState(false);
  
  // Additional businesses state
  const [businesses, setBusinesses] = useState(user.businesses || []);
  const [showAddBusiness, setShowAddBusiness] = useState(false);
  const [newBusiness, setNewBusiness] = useState({
    name: '',
    email: '',
    description: '',
    category: '',
    website: '',
    phone: '',
    facebook: '',
    instagram: '',
    linkedin: '',
    tiktok: '',
    tags: []
  });
  const [addingBusiness, setAddingBusiness] = useState(false);
  const [newBusinessLogoFile, setNewBusinessLogoFile] = useState(null);
  const [newBusinessLogoPreview, setNewBusinessLogoPreview] = useState(null);
  const [tagInput, setTagInput] = useState('');
  
  // Edit business state
  const [editingBusiness, setEditingBusiness] = useState(null);
  const [editBusinessForm, setEditBusinessForm] = useState({});
  const [savingBusiness, setSavingBusiness] = useState(false);
  const [bizTagInput, setBizTagInput] = useState('');
  const [newBizTagInput, setNewBizTagInput] = useState('');

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleAddTag = () => {
    const tag = tagInput.trim();
    if (!tag) return;
    if (formData.tags.length >= 10) {
      toast.error('Maximum 10 tags allowed');
      return;
    }
    if (formData.tags.includes(tag)) {
      toast.error('Tag already added');
      return;
    }
    setFormData({ ...formData, tags: [...formData.tags, tag] });
    setTagInput('');
  };

  const handleRemoveTag = (tagToRemove) => {
    setFormData({ ...formData, tags: formData.tags.filter(t => t !== tagToRemove) });
  };

  const handleTagKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTag();
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }
    
    if (passwordData.new_password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setChangingPassword(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      });
      toast.success('Password changed successfully!');
      setPasswordDialog(false);
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  const handleImageUpload = async (e, type) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    setUploading(true);
    const uploadFormData = new FormData();
    uploadFormData.append('file', file);

    try {
      let endpoint;
      let successMessage;
      if (type === 'profile') {
        endpoint = '/upload/profile-image';
        successMessage = 'Profile picture updated!';
      } else if (type === 'cover') {
        endpoint = '/upload/cover-image';
        successMessage = 'Cover image updated!';
      } else {
        endpoint = '/upload/business-logo';
        successMessage = 'Business logo updated!';
      }
      
      await axios.post(`${API}${endpoint}`, uploadFormData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
      toast.success(successMessage);
    } catch (error) {
      toast.error('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.patch(`${API}/auth/me`, formData);
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
      toast.success('Profile updated successfully!');
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  // Additional business functions
  const handleAddBusiness = async () => {
    if (!newBusiness.name.trim()) {
      toast.error('Business name is required');
      return;
    }
    
    setAddingBusiness(true);
    try {
      const response = await axios.post(`${API}/user/businesses`, newBusiness);
      const createdBusiness = response.data.business;
      
      // If a logo was selected, upload it
      if (newBusinessLogoFile) {
        const formData = new FormData();
        formData.append('file', newBusinessLogoFile);
        
        try {
          const logoResponse = await axios.post(
            `${API}/user/businesses/${createdBusiness.id}/logo`,
            formData,
            { headers: { 'Content-Type': 'multipart/form-data' } }
          );
          createdBusiness.logo = logoResponse.data.logo_url;
        } catch (logoError) {
          console.error('Logo upload failed:', logoError);
          // Business is created, just logo upload failed
        }
      }
      
      setBusinesses([...businesses, createdBusiness]);
      setNewBusiness({ name: '', email: '', description: '', category: '', website: '', phone: '', facebook: '', instagram: '', linkedin: '', tiktok: '', tags: [] });
      setNewBusinessLogoFile(null);
      setNewBusinessLogoPreview(null);
      setShowAddBusiness(false);
      toast.success('Business added!');
      
      // Refresh user data
      const userResponse = await axios.get(`${API}/auth/me`);
      setUser(userResponse.data);
    } catch (error) {
      toast.error('Failed to add business');
    } finally {
      setAddingBusiness(false);
    }
  };

  const handleNewBusinessLogoSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    
    setNewBusinessLogoFile(file);
    // Create preview URL
    const previewUrl = URL.createObjectURL(file);
    setNewBusinessLogoPreview(previewUrl);
  };

  const handleDeleteBusiness = async (businessId) => {
    if (!confirm('Are you sure you want to remove this business?')) return;
    
    try {
      await axios.delete(`${API}/user/businesses/${businessId}`);
      setBusinesses(businesses.filter(b => b.id !== businessId));
      toast.success('Business removed');
      
      // Refresh user data
      const userResponse = await axios.get(`${API}/auth/me`);
      setUser(userResponse.data);
    } catch (error) {
      toast.error('Failed to remove business');
    }
  };

  const handleBusinessLogoUpload = async (e, businessId) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    const uploadFormData = new FormData();
    uploadFormData.append('file', file);

    try {
      const response = await axios.post(`${API}/user/businesses/${businessId}/logo`, uploadFormData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      // Update local state
      setBusinesses(businesses.map(b => 
        b.id === businessId ? { ...b, logo: response.data.logo_url } : b
      ));
      
      // Refresh user data
      const userResponse = await axios.get(`${API}/auth/me`);
      setUser(userResponse.data);
      toast.success('Business logo updated!');
    } catch (error) {
      toast.error('Failed to upload logo');
    }
  };

  const openEditBusiness = (biz) => {
    setEditingBusiness(biz);
    setEditBusinessForm({
      name: biz.name || '',
      email: biz.email || '',
      description: biz.description || '',
      category: biz.category || '',
      website: biz.website || '',
      phone: biz.phone || '',
      facebook: biz.facebook || '',
      instagram: biz.instagram || '',
      linkedin: biz.linkedin || '',
      tiktok: biz.tiktok || '',
      tags: biz.tags || []
    });
    setBizTagInput('');
  };

  const handleSaveEditBusiness = async () => {
    if (!editingBusiness) return;
    setSavingBusiness(true);
    try {
      await axios.patch(`${API}/user/businesses/${editingBusiness.id}`, editBusinessForm);
      setBusinesses(businesses.map(b =>
        b.id === editingBusiness.id ? { ...b, ...editBusinessForm } : b
      ));
      setEditingBusiness(null);
      toast.success('Business updated!');
      const userResponse = await axios.get(`${API}/auth/me`);
      setUser(userResponse.data);
    } catch (error) {
      toast.error('Failed to update business');
    } finally {
      setSavingBusiness(false);
    }
  };

  const handleBizTagAdd = () => {
    const tag = bizTagInput.trim();
    if (!tag) return;
    const current = editBusinessForm.tags || [];
    if (current.length >= 10) { toast.error('Max 10 tags'); return; }
    if (current.includes(tag)) { toast.error('Tag already exists'); return; }
    setEditBusinessForm({ ...editBusinessForm, tags: [...current, tag] });
    setBizTagInput('');
  };

  // Use display_name if set, otherwise full_name
  const displayNameToShow = user.display_name || user.full_name;
  const isNewMember = user.must_change_password || user.profile_incomplete;

  return (
    <div className="max-w-4xl mx-auto px-6" data-testid="profile-page">
      {isNewMember && (
        <div className="mb-6 p-6 bg-gradient-to-r from-[#5b7f95] to-[#4a6b80] rounded-2xl text-white shadow-lg">
          <h2 className="text-2xl font-bold mb-2">Welcome to Mom's Biz Network! 🎉</h2>
          <p className="text-white/90 mb-4">
            Please complete your profile setup:
          </p>
          <ul className="space-y-2 text-sm">
            <li className="flex items-center gap-2">
              {user.profile_image ? '✅' : '⬜'} Upload your profile photo
            </li>
            <li className="flex items-center gap-2">
              {user.business_logo ? '✅' : '⬜'} Add your business logo
            </li>
            <li className="flex items-center gap-2">
              {!user.must_change_password ? '✅' : '⬜'} Change your password (current: MBN2026)
            </li>
            <li className="flex items-center gap-2">
              {user.business_description ? '✅' : '⬜'} Add your business description
            </li>
          </ul>
        </div>
      )}

      <h1 className="text-4xl font-bold text-[#2d3748] mb-8">
        My Profile
      </h1>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Profile Info Card */}
        <Card className="border-gray-100 rounded-2xl shadow-sm overflow-hidden">
          {/* Cover Image - More prominent with better edit button */}
          <div className="relative h-32 bg-gradient-to-br from-[#5b7f95] to-[#4a6b80]">
            {user.cover_image && (
              <img 
                src={getImageUrl(user.cover_image)} 
                alt="Cover"
                className="absolute inset-0 w-full h-full object-cover"
              />
            )}
            <label htmlFor="cover-upload" className="absolute bottom-2 right-2 cursor-pointer">
              <div className="flex items-center gap-2 px-3 py-1.5 bg-white/90 hover:bg-white rounded-full shadow-md transition-colors">
                <ImagePlus className="w-4 h-4 text-[#5b7f95]" />
                <span className="text-xs font-medium text-[#5b7f95]">
                  {uploading ? 'Uploading...' : (user.cover_image ? 'Change Banner' : 'Add Banner')}
                </span>
              </div>
              <input
                id="cover-upload"
                type="file"
                accept="image/*"
                onChange={(e) => handleImageUpload(e, 'cover')}
                className="hidden"
                disabled={uploading}
              />
            </label>
          </div>
          
          <CardContent className="pt-0">
            <div className="text-center -mt-12">
              <div className="relative h-24 w-24 mx-auto mb-3 rounded-full overflow-hidden bg-gray-100 border-4 border-white shadow-md">
                {user.profile_image ? (
                  <img 
                    src={getImageUrl(user.profile_image)} 
                    alt={displayNameToShow}
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center bg-[#5b7f95] text-white text-3xl font-semibold">
                    {displayNameToShow.charAt(0)}
                  </div>
                )}
              </div>
              
              <label htmlFor="profile-upload" className="cursor-pointer">
                <div className="inline-block px-4 py-2 bg-[#5b7f95] text-white rounded-full text-sm hover:bg-[#4a6b80] transition-colors">
                  {uploading ? 'Uploading...' : 'Change Photo'}
                </div>
                <input
                  id="profile-upload"
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleImageUpload(e, 'profile')}
                  className="hidden"
                  disabled={uploading}
                />
              </label>
              
              <p className="text-xs text-[#64748b] mt-2 mb-3">Cover: 1200×300px recommended</p>
              
              <h2 className="text-xl font-semibold text-[#2d3748] mb-1">{displayNameToShow}</h2>
              <p className="text-sm text-[#64748b] mb-2">{user.display_email || user.email}</p>
              <p className="text-sm text-[#5b7f95] mb-4">{user.area}</p>
              {user.is_admin && (
                <span className="inline-block px-3 py-1 bg-[#cb9944] text-white text-xs rounded-full">
                  Admin
                </span>
              )}
              {user.is_area_manager && !user.is_admin && (
                <span className="inline-block px-3 py-1 bg-[#5b7f95] text-white text-xs rounded-full">
                  Chapter Leader
                </span>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Edit Profile Form */}
        <Card className="border-gray-100 rounded-2xl shadow-sm lg:col-span-2">
          <CardHeader>
            <CardTitle>Edit Profile</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Display Name & Full Name */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="display_name">Display Name</Label>
                  <Input
                    id="display_name"
                    name="display_name"
                    value={formData.display_name}
                    onChange={handleChange}
                    placeholder="How you want to be known"
                    className="bg-gray-50 border-gray-200 rounded-xl"
                    data-testid="profile-display-name-input"
                  />
                  <p className="text-xs text-[#64748b] mt-1">This is how your name will appear to others</p>
                </div>

                <div>
                  <Label htmlFor="full_name">Full Name (Legal)</Label>
                  <Input
                    id="full_name"
                    name="full_name"
                    value={formData.full_name}
                    onChange={handleChange}
                    className="bg-gray-50 border-gray-200 rounded-xl"
                    data-testid="profile-fullname-input"
                  />
                </div>
              </div>

              {/* Display Email & Account Email */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="display_email">Business Email (Display)</Label>
                  <Input
                    id="display_email"
                    name="display_email"
                    type="email"
                    value={formData.display_email}
                    onChange={handleChange}
                    placeholder="your@business.com"
                    className="bg-gray-50 border-gray-200 rounded-xl"
                    data-testid="profile-display-email-input"
                  />
                  <p className="text-xs text-[#64748b] mt-1">Shown in directory (optional)</p>
                </div>

                <div>
                  <Label>Login Email</Label>
                  <Input
                    value={user.email}
                    disabled
                    className="bg-gray-100 border-gray-200 rounded-xl text-gray-500"
                  />
                  <p className="text-xs text-[#64748b] mt-1">Used for login only</p>
                </div>
              </div>

              {/* Area */}
              <div>
                <Label htmlFor="area">Area</Label>
                <Select value={formData.area} onValueChange={(value) => setFormData({ ...formData, area: value })}>
                  <SelectTrigger className="bg-gray-50 border-gray-200 rounded-xl" data-testid="profile-area-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {areas.map((area) => (
                      <SelectItem key={area} value={area}>
                        {area}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Phone & Website */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="phone">Phone</Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    className="bg-gray-50 border-gray-200 rounded-xl"
                    data-testid="profile-phone-input"
                  />
                </div>

                <div>
                  <Label htmlFor="website">Website</Label>
                  <Input
                    id="website"
                    name="website"
                    type="url"
                    value={formData.website}
                    onChange={handleChange}
                    placeholder="https://yourwebsite.com"
                    className="bg-gray-50 border-gray-200 rounded-xl"
                    data-testid="profile-website-input"
                  />
                </div>
              </div>

              {/* Business Name & Category */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="business_name">Business Name</Label>
                  <Input
                    id="business_name"
                    name="business_name"
                    value={formData.business_name}
                    onChange={handleChange}
                    className="bg-gray-50 border-gray-200 rounded-xl"
                    data-testid="profile-business-name-input"
                  />
                </div>

                <div>
                  <Label htmlFor="business_category">Business Category</Label>
                  <Select value={formData.business_category} onValueChange={(value) => setFormData({ ...formData, business_category: value })}>
                    <SelectTrigger className="bg-gray-50 border-gray-200 rounded-xl" data-testid="profile-category-select">
                      <SelectValue placeholder="Select a category" />
                    </SelectTrigger>
                    <SelectContent>
                      {BUSINESS_CATEGORIES.map((category) => (
                        <SelectItem key={category} value={category}>
                          {category}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Business Logo */}
              <div>
                <Label>Business Logo</Label>
                <div className="flex gap-2 items-center">
                  <label htmlFor="logo-upload" className="flex-1 cursor-pointer">
                    <div className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-600 hover:bg-gray-100 transition-colors text-center">
                      {uploading ? 'Uploading...' : user.business_logo ? 'Change Logo' : 'Upload Logo'}
                    </div>
                    <input
                      id="logo-upload"
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleImageUpload(e, 'logo')}
                      className="hidden"
                      disabled={uploading}
                    />
                  </label>
                  {user.business_logo && (
                    <img src={getImageUrl(user.business_logo)} alt="Logo" className="h-10 w-10 rounded object-cover border border-gray-200" />
                  )}
                </div>
              </div>

              {/* Business Description */}
              <div>
                <Label htmlFor="business_description">Business Description</Label>
                <Textarea
                  id="business_description"
                  name="business_description"
                  value={formData.business_description}
                  onChange={handleChange}
                  rows={4}
                  className="bg-gray-50 border-gray-200 rounded-xl"
                  data-testid="profile-business-desc-input"
                />
              </div>

              {/* Tags */}
              <div>
                <Label className="flex items-center gap-2 mb-2">
                  <Tag className="w-4 h-4 text-[#5b7f95]" />
                  Business Tags ({formData.tags.length}/10)
                </Label>
                <div className="flex gap-2 mb-3">
                  <Input
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyDown={handleTagKeyDown}
                    placeholder="Type a tag and press Enter"
                    className="bg-gray-50 border-gray-200 rounded-xl flex-1"
                    disabled={formData.tags.length >= 10}
                    data-testid="tag-input"
                  />
                  <Button
                    type="button"
                    onClick={handleAddTag}
                    disabled={!tagInput.trim() || formData.tags.length >= 10}
                    className="bg-[#5b7f95] hover:bg-[#4a6b80] text-white rounded-xl"
                    data-testid="add-tag-btn"
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                {formData.tags.length > 0 && (
                  <div className="flex flex-wrap gap-2" data-testid="tags-list">
                    {formData.tags.map((tag) => (
                      <span
                        key={tag}
                        className="inline-flex items-center gap-1 px-3 py-1.5 bg-[#5b7f95]/10 text-[#5b7f95] text-sm font-medium rounded-full"
                      >
                        {tag}
                        <button
                          type="button"
                          onClick={() => handleRemoveTag(tag)}
                          className="w-4 h-4 rounded-full hover:bg-[#5b7f95]/20 flex items-center justify-center"
                          data-testid="remove-tag-btn"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-[#5b7f95] hover:bg-[#4a6b80] text-white rounded-full py-6"
                data-testid="update-profile-btn"
              >
                {loading ? 'Updating...' : 'Update Profile'}
              </Button>
            </form>

            {/* Additional Businesses Section */}
            <div className="mt-8 pt-6 border-t border-gray-200">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-[#2d3748] flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-[#5b7f95]" />
                  Additional Businesses
                </h3>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowAddBusiness(true)}
                  className="rounded-full border-[#cb9944] text-[#cb9944] hover:bg-[#cb9944]/10"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add Business
                </Button>
              </div>
              
              <p className="text-sm text-[#64748b] mb-4">
                Add additional businesses that will appear separately in the Member Directory.
              </p>

              {/* List of additional businesses */}
              {businesses.length > 0 ? (
                <div className="space-y-4">
                  {businesses.map((biz) => (
                    <div 
                      key={biz.id} 
                      className="p-4 bg-gray-50 rounded-xl border border-gray-100 cursor-pointer hover:border-[#5b7f95]/40 hover:shadow-sm transition-all"
                      onClick={() => openEditBusiness(biz)}
                      data-testid="business-card"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          {biz.logo ? (
                            <img src={getImageUrl(biz.logo)} alt={biz.name} className="w-12 h-12 rounded-lg object-cover" />
                          ) : (
                            <div className="w-12 h-12 rounded-lg bg-[#5b7f95]/10 flex items-center justify-center">
                              <Building2 className="w-6 h-6 text-[#5b7f95]" />
                            </div>
                          )}
                          <div>
                            <h4 className="font-semibold text-[#2d3748]">{biz.name}</h4>
                            {biz.category && (
                              <span className="text-xs text-[#5b7f95]">{biz.category}</span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => { e.stopPropagation(); openEditBusiness(biz); }}
                            className="text-[#5b7f95] hover:text-[#4a6b80] hover:bg-[#5b7f95]/10"
                            data-testid="edit-business-btn"
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => { e.stopPropagation(); handleDeleteBusiness(biz.id); }}
                            className="text-red-500 hover:text-red-600 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                      {biz.description && (
                        <p className="text-sm text-[#64748b] mt-2">{biz.description}</p>
                      )}
                      {(biz.tags && biz.tags.length > 0) && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {biz.tags.map(t => (
                            <span key={t} className="px-2 py-0.5 bg-[#5b7f95]/10 text-[#5b7f95] text-xs rounded-full">{t}</span>
                          ))}
                        </div>
                      )}
                      <p className="text-xs text-[#5b7f95] mt-2">Click to edit all details</p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-[#64748b] text-center py-4">
                  No additional businesses yet. Click "Add Business" to add one.
                </p>
              )}

              {/* Add Business Dialog */}
              <Dialog open={showAddBusiness} onOpenChange={setShowAddBusiness}>
                <DialogContent className="sm:max-w-lg">
                  <DialogHeader>
                    <DialogTitle>Add New Business</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 mt-4">
                    <div>
                      <Label htmlFor="new_biz_name">Business Name *</Label>
                      <Input
                        id="new_biz_name"
                        value={newBusiness.name}
                        onChange={(e) => setNewBusiness({ ...newBusiness, name: e.target.value })}
                        className="bg-gray-50 border-gray-200 rounded-xl"
                        placeholder="Your business name"
                      />
                    </div>
                    <div>
                      <Label htmlFor="new_biz_email">Business Email</Label>
                      <Input
                        id="new_biz_email"
                        type="email"
                        value={newBusiness.email}
                        onChange={(e) => setNewBusiness({ ...newBusiness, email: e.target.value })}
                        className="bg-gray-50 border-gray-200 rounded-xl"
                        placeholder="business@email.com"
                      />
                    </div>
                    <div>
                      <Label htmlFor="new_biz_category">Category</Label>
                      <Select value={newBusiness.category} onValueChange={(value) => setNewBusiness({ ...newBusiness, category: value })}>
                        <SelectTrigger className="bg-gray-50 border-gray-200 rounded-xl">
                          <SelectValue placeholder="Select a category" />
                        </SelectTrigger>
                        <SelectContent>
                          {BUSINESS_CATEGORIES.map((cat) => (
                            <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="new_biz_desc">Description (3 sentences)</Label>
                      <Textarea
                        id="new_biz_desc"
                        value={newBusiness.description}
                        onChange={(e) => setNewBusiness({ ...newBusiness, description: e.target.value })}
                        className="bg-gray-50 border-gray-200 rounded-xl"
                        rows={3}
                        placeholder="Brief description of your business..."
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="new_biz_phone">Phone</Label>
                        <Input
                          id="new_biz_phone"
                          value={newBusiness.phone}
                          onChange={(e) => setNewBusiness({ ...newBusiness, phone: e.target.value })}
                          className="bg-gray-50 border-gray-200 rounded-xl"
                        />
                      </div>
                      <div>
                        <Label htmlFor="new_biz_website">Website</Label>
                        <Input
                          id="new_biz_website"
                          value={newBusiness.website}
                          onChange={(e) => setNewBusiness({ ...newBusiness, website: e.target.value })}
                          className="bg-gray-50 border-gray-200 rounded-xl"
                        />
                      </div>
                    </div>
                    {/* Social Media for new business */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-xs">Facebook</Label>
                        <Input
                          value={newBusiness.facebook}
                          onChange={(e) => setNewBusiness({ ...newBusiness, facebook: e.target.value })}
                          className="bg-gray-50 border-gray-200 rounded-xl"
                          placeholder="Facebook URL"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Instagram</Label>
                        <Input
                          value={newBusiness.instagram}
                          onChange={(e) => setNewBusiness({ ...newBusiness, instagram: e.target.value })}
                          className="bg-gray-50 border-gray-200 rounded-xl"
                          placeholder="Instagram URL"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">LinkedIn</Label>
                        <Input
                          value={newBusiness.linkedin}
                          onChange={(e) => setNewBusiness({ ...newBusiness, linkedin: e.target.value })}
                          className="bg-gray-50 border-gray-200 rounded-xl"
                          placeholder="LinkedIn URL"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">TikTok</Label>
                        <Input
                          value={newBusiness.tiktok}
                          onChange={(e) => setNewBusiness({ ...newBusiness, tiktok: e.target.value })}
                          className="bg-gray-50 border-gray-200 rounded-xl"
                          placeholder="TikTok URL"
                        />
                      </div>
                    </div>
                    {/* Tags for new business */}
                    <div>
                      <Label>
                        Business Tags ({(newBusiness.tags || []).length}/10)
                      </Label>
                      <div className="flex gap-2 mt-1">
                        <Input
                          value={newBizTagInput}
                          onChange={(e) => setNewBizTagInput(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              const tag = newBizTagInput.trim();
                              if (!tag || (newBusiness.tags || []).length >= 10 || (newBusiness.tags || []).includes(tag)) return;
                              setNewBusiness({ ...newBusiness, tags: [...(newBusiness.tags || []), tag] });
                              setNewBizTagInput('');
                            }
                          }}
                          placeholder="Type a tag and press Enter"
                          className="bg-gray-50 border-gray-200 rounded-xl"
                          disabled={(newBusiness.tags || []).length >= 10}
                        />
                        <Button type="button" variant="outline" size="sm" className="rounded-xl"
                          onClick={() => {
                            const tag = newBizTagInput.trim();
                            if (!tag || (newBusiness.tags || []).length >= 10 || (newBusiness.tags || []).includes(tag)) return;
                            setNewBusiness({ ...newBusiness, tags: [...(newBusiness.tags || []), tag] });
                            setNewBizTagInput('');
                          }}>Add</Button>
                      </div>
                      {(newBusiness.tags || []).length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {(newBusiness.tags || []).map((tag) => (
                            <span key={`newtag-${tag}`} className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#cb9944]/10 text-[#cb9944] text-xs font-medium rounded-full">
                              {tag}
                              <button type="button" onClick={() => setNewBusiness({ ...newBusiness, tags: newBusiness.tags.filter(t => t !== tag) })}
                                className="hover:text-red-500">
                                <X className="w-3 h-3" />
                              </button>
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    <div>
                      <Label>Business Logo</Label>
                      <div className="flex items-center gap-3 mt-2">
                        {newBusinessLogoPreview ? (
                          <img 
                            src={newBusinessLogoPreview} 
                            alt="Logo preview" 
                            className="w-16 h-16 rounded-lg object-cover border border-gray-200"
                          />
                        ) : (
                          <div className="w-16 h-16 rounded-lg bg-gray-100 flex items-center justify-center border border-gray-200">
                            <ImagePlus className="w-6 h-6 text-gray-400" />
                          </div>
                        )}
                        <label htmlFor="new-biz-logo-upload" className="flex-1 cursor-pointer">
                          <div className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-600 hover:bg-gray-100 transition-colors text-center">
                            {newBusinessLogoPreview ? 'Change Logo' : 'Upload Logo'}
                          </div>
                          <input
                            id="new-biz-logo-upload"
                            type="file"
                            accept="image/*"
                            onChange={handleNewBusinessLogoSelect}
                            className="hidden"
                          />
                        </label>
                      </div>
                    </div>
                    <Button
                      onClick={handleAddBusiness}
                      disabled={addingBusiness}
                      className="w-full bg-[#cb9944] hover:bg-[#b08435] text-white rounded-full"
                    >
                      {addingBusiness ? 'Adding...' : 'Add Business'}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>

              {/* Edit Business Dialog */}
              <Dialog open={!!editingBusiness} onOpenChange={(open) => { if (!open) setEditingBusiness(null); }}>
                <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Edit Business</DialogTitle>
                  </DialogHeader>
                  {editingBusiness && (
                    <div className="space-y-4 mt-4">
                      <div>
                        <Label>Business Name *</Label>
                        <Input
                          value={editBusinessForm.name}
                          onChange={(e) => setEditBusinessForm({ ...editBusinessForm, name: e.target.value })}
                          className="bg-gray-50 border-gray-200 rounded-xl"
                        />
                      </div>
                      <div>
                        <Label>Business Email</Label>
                        <Input
                          type="email"
                          value={editBusinessForm.email}
                          onChange={(e) => setEditBusinessForm({ ...editBusinessForm, email: e.target.value })}
                          className="bg-gray-50 border-gray-200 rounded-xl"
                        />
                      </div>
                      <div>
                        <Label>Category</Label>
                        <Select value={editBusinessForm.category} onValueChange={(value) => setEditBusinessForm({ ...editBusinessForm, category: value })}>
                          <SelectTrigger className="bg-gray-50 border-gray-200 rounded-xl">
                            <SelectValue placeholder="Select a category" />
                          </SelectTrigger>
                          <SelectContent>
                            {BUSINESS_CATEGORIES.map((cat) => (
                              <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Description</Label>
                        <Textarea
                          value={editBusinessForm.description}
                          onChange={(e) => setEditBusinessForm({ ...editBusinessForm, description: e.target.value })}
                          className="bg-gray-50 border-gray-200 rounded-xl"
                          rows={3}
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Phone</Label>
                          <Input
                            value={editBusinessForm.phone}
                            onChange={(e) => setEditBusinessForm({ ...editBusinessForm, phone: e.target.value })}
                            className="bg-gray-50 border-gray-200 rounded-xl"
                          />
                        </div>
                        <div>
                          <Label>Website</Label>
                          <Input
                            value={editBusinessForm.website}
                            onChange={(e) => setEditBusinessForm({ ...editBusinessForm, website: e.target.value })}
                            className="bg-gray-50 border-gray-200 rounded-xl"
                          />
                        </div>
                      </div>

                      {/* Social Media */}
                      <div className="border-t pt-4">
                        <Label className="text-[#5b7f95] font-semibold mb-3 block">Social Media</Label>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label className="text-xs">Facebook</Label>
                            <Input
                              value={editBusinessForm.facebook}
                              onChange={(e) => setEditBusinessForm({ ...editBusinessForm, facebook: e.target.value })}
                              className="bg-gray-50 border-gray-200 rounded-xl"
                              placeholder="Facebook URL"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Instagram</Label>
                            <Input
                              value={editBusinessForm.instagram}
                              onChange={(e) => setEditBusinessForm({ ...editBusinessForm, instagram: e.target.value })}
                              className="bg-gray-50 border-gray-200 rounded-xl"
                              placeholder="Instagram URL"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">LinkedIn</Label>
                            <Input
                              value={editBusinessForm.linkedin}
                              onChange={(e) => setEditBusinessForm({ ...editBusinessForm, linkedin: e.target.value })}
                              className="bg-gray-50 border-gray-200 rounded-xl"
                              placeholder="LinkedIn URL"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">TikTok</Label>
                            <Input
                              value={editBusinessForm.tiktok}
                              onChange={(e) => setEditBusinessForm({ ...editBusinessForm, tiktok: e.target.value })}
                              className="bg-gray-50 border-gray-200 rounded-xl"
                              placeholder="TikTok URL"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Tags */}
                      <div className="border-t pt-4">
                        <Label className="flex items-center gap-2 mb-2">
                          <Tag className="w-4 h-4 text-[#5b7f95]" />
                          Business Tags ({(editBusinessForm.tags || []).length}/10)
                        </Label>
                        <div className="flex gap-2 mb-3">
                          <Input
                            value={bizTagInput}
                            onChange={(e) => setBizTagInput(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleBizTagAdd(); } }}
                            placeholder="Type a tag and press Enter"
                            className="bg-gray-50 border-gray-200 rounded-xl flex-1"
                            disabled={(editBusinessForm.tags || []).length >= 10}
                          />
                          <Button
                            type="button"
                            onClick={handleBizTagAdd}
                            disabled={!bizTagInput.trim() || (editBusinessForm.tags || []).length >= 10}
                            className="bg-[#5b7f95] hover:bg-[#4a6b80] text-white rounded-xl"
                          >
                            <Plus className="w-4 h-4" />
                          </Button>
                        </div>
                        {(editBusinessForm.tags || []).length > 0 && (
                          <div className="flex flex-wrap gap-2">
                            {editBusinessForm.tags.map((tag) => (
                              <span key={tag} className="inline-flex items-center gap-1 px-3 py-1.5 bg-[#5b7f95]/10 text-[#5b7f95] text-sm font-medium rounded-full">
                                {tag}
                                <button
                                  type="button"
                                  onClick={() => setEditBusinessForm({ ...editBusinessForm, tags: editBusinessForm.tags.filter(t => t !== tag) })}
                                  className="w-4 h-4 rounded-full hover:bg-[#5b7f95]/20 flex items-center justify-center"
                                >
                                  <X className="w-3 h-3" />
                                </button>
                              </span>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Logo Upload */}
                      <div className="border-t pt-4">
                        <Label>Business Logo</Label>
                        <div className="flex items-center gap-3 mt-2">
                          {editingBusiness.logo ? (
                            <img src={getImageUrl(editingBusiness.logo)} alt="Logo" className="w-16 h-16 rounded-lg object-cover border border-gray-200" />
                          ) : (
                            <div className="w-16 h-16 rounded-lg bg-gray-100 flex items-center justify-center border border-gray-200">
                              <Building2 className="w-6 h-6 text-gray-400" />
                            </div>
                          )}
                          <label className="flex-1 cursor-pointer">
                            <div className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-600 hover:bg-gray-100 transition-colors text-center">
                              {editingBusiness.logo ? 'Change Logo' : 'Upload Logo'}
                            </div>
                            <input
                              type="file"
                              accept="image/*"
                              onChange={(e) => {
                                handleBusinessLogoUpload(e, editingBusiness.id);
                              }}
                              className="hidden"
                            />
                          </label>
                        </div>
                      </div>

                      <div className="flex gap-3 pt-4">
                        <Button
                          variant="outline"
                          onClick={() => setEditingBusiness(null)}
                          className="flex-1 rounded-full"
                        >
                          Cancel
                        </Button>
                        <Button
                          onClick={handleSaveEditBusiness}
                          disabled={savingBusiness || !editBusinessForm.name?.trim()}
                          className="flex-1 bg-[#cb9944] hover:bg-[#b08435] text-white rounded-full"
                          data-testid="save-edit-business-btn"
                        >
                          {savingBusiness ? 'Saving...' : 'Save Changes'}
                        </Button>
                      </div>
                    </div>
                  )}
                </DialogContent>
              </Dialog>
            </div>

            {/* Change Password Section */}
            <div className="mt-8 pt-6 border-t border-gray-200">
              <Dialog open={passwordDialog} onOpenChange={setPasswordDialog}>
                <DialogTrigger asChild>
                  <Button 
                    variant="outline" 
                    className="w-full rounded-full border-[#5b7f95] text-[#5b7f95] hover:bg-[#5b7f95]/10"
                    data-testid="change-password-btn"
                  >
                    <Lock className="w-4 h-4 mr-2" />
                    Change Password
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-md">
                  <DialogHeader>
                    <DialogTitle>Change Password</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handlePasswordChange} className="space-y-4 mt-4">
                    <div>
                      <Label htmlFor="current_password">Current Password</Label>
                      <Input
                        id="current_password"
                        type="password"
                        value={passwordData.current_password}
                        onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                        required
                        className="bg-gray-50 border-gray-200 rounded-xl"
                        data-testid="current-password-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="new_password">New Password</Label>
                      <Input
                        id="new_password"
                        type="password"
                        value={passwordData.new_password}
                        onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                        required
                        minLength={6}
                        className="bg-gray-50 border-gray-200 rounded-xl"
                        placeholder="At least 6 characters"
                        data-testid="new-password-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="confirm_password">Confirm New Password</Label>
                      <Input
                        id="confirm_password"
                        type="password"
                        value={passwordData.confirm_password}
                        onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                        required
                        className="bg-gray-50 border-gray-200 rounded-xl"
                        data-testid="confirm-new-password-input"
                      />
                    </div>
                    <div className="flex gap-3 pt-2">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setPasswordDialog(false)}
                        className="flex-1 rounded-full"
                      >
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        disabled={changingPassword}
                        className="flex-1 bg-[#5b7f95] hover:bg-[#4a6b80] text-white rounded-full"
                        data-testid="submit-password-change-btn"
                      >
                        {changingPassword ? 'Updating...' : 'Update Password'}
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
